import { useState, useEffect, useCallback, useRef } from 'react';
import jscanify from 'jscanify/src/jscanify.js';
import {
  getCameraStream,
  stopCameraStream,
  canvasToPDFWithSizeLimit,
  generateScanFilename,
} from '../utils/scannerUtils';
import { captureVideoFrame } from '../utils/imageProcessing';
import {
  PaperSizeKey,
  getPaperSize,
  DEFAULT_PAPER_SIZE,
} from '../utils/paperSizes';
import {
  createPDFInstance,
  getOptimalOrientation,
  addImageToPDF,
  PDF_QUALITY,
} from '../../../utils/pdf-utils';

export type ScannerState =
  | 'idle'
  | 'initializing'
  | 'camera'
  | 'processing'
  | 'preview'
  | 'summary'
  | 'error';

// OpenCV Mat type
interface Mat {
  delete(): void;
  cols: number;
  rows: number;
}

// Type for a 2D point
interface Point {
  x: number;
  y: number;
}

// Type for corner points returned by jscanify
interface CornerPoints {
  topLeftCorner: Point;
  topRightCorner: Point;
  bottomLeftCorner: Point;
  bottomRightCorner: Point;
}

// Type for jscanify scanner instance
interface Scanner {
  highlightPaper: (canvas: HTMLCanvasElement) => HTMLCanvasElement;
  extractPaper: (
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    cornerPoints?: CornerPoints
  ) => HTMLCanvasElement;
  findPaperContour: (img: Mat) => Mat | null;
  getCornerPoints: (contour: Mat) => CornerPoints;
}

interface UseDocumentScannerOptions {
  preferredCamera?: 'user' | 'environment';
  paperSize?: PaperSizeKey;
  onScanComplete?: (file: File) => void;
  onError?: (error: Error) => void;
}

export interface ScannedPage {
  id: string;
  capturedImage: string;
  processedImage: string;
  timestamp: number;
}

export const useDocumentScanner = ({
  preferredCamera = 'environment',
  paperSize = DEFAULT_PAPER_SIZE,
  onScanComplete,
  onError,
}: UseDocumentScannerOptions = {}) => {
  const [state, setState] = useState<ScannerState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [scannedPages, setScannedPages] = useState<ScannedPage[]>([]);
  const [documentMode, setDocumentMode] = useState<boolean>(true);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scannerRef = useRef<Scanner | null>(null);

  const initializeScanner = useCallback(async () => {
    try {
      setState('initializing');
      setError(null);

      // Wait for OpenCV to load with 10-second timeout
      // Note: Using setTimeout instead of @mantine/hooks useTimeout because we need
      // imperative timeout control inside a Promise constructor (not at hook level)
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(
            new Error('OpenCV load timeout - failed to load within 10 seconds')
          );
        }, 10000);

        const checkOpenCV = () => {
          if ((window as any).cv) {
            clearTimeout(timeout);
            resolve();
          }
        };

        if (typeof window !== 'undefined' && (window as any).cv) {
          // OpenCV already loaded
          clearTimeout(timeout);
          resolve();
        } else if (document.readyState === 'complete') {
          // Page already loaded, check if OpenCV is available
          checkOpenCV();
          // If not available after page load, wait a bit for async script
          setTimeout(() => {
            if (!(window as any).cv) {
              clearTimeout(timeout);
              reject(new Error('OpenCV not available after page load'));
            }
          }, 1000);
        } else {
          // Wait for page load, then check OpenCV
          window.addEventListener('load', checkOpenCV, { once: true });
        }
      });

      // Initialize jscanify scanner after OpenCV is loaded
      const scanner = new jscanify();
      scannerRef.current = scanner;

      // Get camera stream
      const cameraStream = await getCameraStream(preferredCamera);
      setStream(cameraStream);

      setState('camera');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initialize scanner';
      setError(errorMessage);
      setState('error');
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    }
  }, [preferredCamera, onError]);

  const captureImage = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) {
      setError('Camera not ready');
      return;
    }

    // Track OpenCV Mat objects for cleanup
    const matsToCleanup: Mat[] = [];

    try {
      setState('processing');
      setError(null);

      // Capture frame from video
      captureVideoFrame(videoRef.current, canvasRef.current);

      // If document mode is OFF, just capture the raw image
      if (!documentMode) {
        const capturedDataURL = canvasRef.current.toDataURL('image/jpeg', 0.95);
        setCapturedImage(capturedDataURL);
        setProcessedImage(capturedDataURL);
        setState('preview');
        return;
      }

      // Document mode is ON - perform document detection
      if (!scannerRef.current) {
        // Scanner not ready, fall back to raw capture
        const capturedDataURL = canvasRef.current.toDataURL('image/jpeg', 0.95);
        setCapturedImage(capturedDataURL);
        setProcessedImage(capturedDataURL);
        setState('preview');
        return;
      }

      // Convert canvas to OpenCV Mat
      const cv = (window as any).cv;
      if (!cv) {
        throw new Error('OpenCV not loaded');
      }

      const imgMat: Mat = cv.imread(canvasRef.current);
      matsToCleanup.push(imgMat);

      // Detect document contour
      const contour = scannerRef.current.findPaperContour(imgMat);

      if (!contour) {
        // No document detected, use captured image as-is
        const capturedDataURL = canvasRef.current.toDataURL('image/jpeg', 0.95);
        setCapturedImage(capturedDataURL);
        setProcessedImage(capturedDataURL);
      } else {
        matsToCleanup.push(contour);

        // Get corner points from the contour
        const cornerPoints = scannerRef.current.getCornerPoints(contour);

        const size = getPaperSize(paperSize);

        // Extract and correct the paper using the detected corner points
        // If paperSize is AUTO (0x0), use canvas dimensions
        const resultCanvas = scannerRef.current.extractPaper(
          canvasRef.current,
          size.width || canvasRef.current.width,
          size.height || canvasRef.current.height,
          cornerPoints
        );

        // Convert processed result to data URL
        const processedDataURL = resultCanvas.toDataURL('image/jpeg', 0.95);
        setProcessedImage(processedDataURL);

        // Create a rotated version of the original to match processed orientation
        // Determine if processed image is portrait or landscape
        const isProcessedPortrait = resultCanvas.height > resultCanvas.width;
        const isOriginalPortrait =
          canvasRef.current.height > canvasRef.current.width;

        // If orientations differ, rotate the original to match
        let capturedDataURL: string;

        if (isProcessedPortrait !== isOriginalPortrait) {
          // Create a new canvas for the rotated image
          const rotatedCanvas = document.createElement('canvas');
          rotatedCanvas.width = canvasRef.current.height;
          rotatedCanvas.height = canvasRef.current.width;

          const rotatedCtx = rotatedCanvas.getContext('2d');
          if (rotatedCtx) {
            // Rotate 90 degrees clockwise
            rotatedCtx.translate(
              rotatedCanvas.width / 2,
              rotatedCanvas.height / 2
            );
            rotatedCtx.rotate(Math.PI / 2);
            rotatedCtx.drawImage(
              canvasRef.current,
              -canvasRef.current.width / 2,
              -canvasRef.current.height / 2
            );

            capturedDataURL = rotatedCanvas.toDataURL('image/jpeg', 0.95);
          } else {
            // Rotation failed, use original
            capturedDataURL = canvasRef.current.toDataURL('image/jpeg', 0.95);
          }
        } else {
          // Orientations match, use original as-is
          capturedDataURL = canvasRef.current.toDataURL('image/jpeg', 0.95);
        }

        setCapturedImage(capturedDataURL);
      }

      setState('preview');
    } catch (err) {
      console.error('Error capturing/processing image:', err);
      setError('Failed to process document. Please try again.');
      setState('camera');
    } finally {
      // Clean up OpenCV Mat objects to prevent memory leaks
      matsToCleanup.forEach((mat) => {
        try {
          mat.delete();
        } catch (e) {
          console.error('Error deleting Mat object:', e);
        }
      });
    }
  }, [paperSize, documentMode]);

  const highlightDocument = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || !scannerRef.current) {
      return null;
    }

    try {
      // 1. Draw video frame to canvas
      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) {
        return null;
      }

      ctx.drawImage(
        videoRef.current,
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );

      // 2. Enhance contrast to help detect white documents on white backgrounds
      // This increases edge visibility for the Canny edge detector
      const imageData = ctx.getImageData(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );
      const data = imageData.data;

      // Apply moderate contrast enhancement (1.4x)
      const contrastFactor = 1.4;
      const brightnessOffset = -10; // Slightly darken to increase edge contrast

      for (let i = 0; i < data.length; i += 4) {
        // Apply contrast and brightness to RGB channels
        data[i] = Math.max(
          0,
          Math.min(
            255,
            (data[i] - 128) * contrastFactor + 128 + brightnessOffset
          )
        ); // Red
        data[i + 1] = Math.max(
          0,
          Math.min(
            255,
            (data[i + 1] - 128) * contrastFactor + 128 + brightnessOffset
          )
        ); // Green
        data[i + 2] = Math.max(
          0,
          Math.min(
            255,
            (data[i + 2] - 128) * contrastFactor + 128 + brightnessOffset
          )
        ); // Blue
      }

      ctx.putImageData(imageData, 0, 0);

      // 3. Get highlighted result from jscanify
      const resultCanvas = scannerRef.current.highlightPaper(canvasRef.current);

      return resultCanvas;
    } catch (err) {
      console.error('Error highlighting document:', err);
      return null;
    }
  }, []);

  // Add current page to batch and go to summary
  const addPageToBatch = useCallback(() => {
    if (!capturedImage || !processedImage) {
      setError('No image to add to batch');
      return;
    }

    const newPage: ScannedPage = {
      id: `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      capturedImage,
      processedImage,
      timestamp: Date.now(),
    };

    setScannedPages((prev) => [...prev, newPage]);
    setCapturedImage(null);
    setProcessedImage(null);
    setState('summary');
  }, [capturedImage, processedImage]);

  // Go to summary page (from preview, keeping current image as pending)
  const goToSummary = useCallback(() => {
    // If there's a current image, add it to the batch first
    if (capturedImage && processedImage) {
      const newPage: ScannedPage = {
        id: `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        capturedImage,
        processedImage,
        timestamp: Date.now(),
      };
      setScannedPages((prev) => [...prev, newPage]);
      setCapturedImage(null);
      setProcessedImage(null);
    }
    setState('summary');
  }, [capturedImage, processedImage]);

  // From summary, go back to camera to add more pages
  const addMorePages = useCallback(() => {
    setState('camera');
  }, []);

  const removePageFromBatch = useCallback((pageId: string) => {
    setScannedPages((prev) => prev.filter((page) => page.id !== pageId));
  }, []);

  const acceptScan = useCallback(async () => {
    if (!processedImage) {
      setError('No processed image available');
      return;
    }

    try {
      setState('processing');
      setError(null);

      // Create image element from data URL
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load processed image'));
        img.src = processedImage;
      });

      // Create canvas from image
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      ctx.drawImage(img, 0, 0);

      // Convert to PDF with size validation and compression
      const filename = generateScanFilename();
      const pdfFile = await canvasToPDFWithSizeLimit(canvas, filename);

      // Call completion callback
      onScanComplete?.(pdfFile);

      // Reset state
      setState('idle');
      setCapturedImage(null);
      setProcessedImage(null);
      setScannedPages([]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create PDF';
      setError(errorMessage);
      setState('error');
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    }
  }, [processedImage, onScanComplete, onError]);

  const finalizeBatchScan = useCallback(async () => {
    // Build the final pages array - include current preview if it exists
    const finalPages = [...scannedPages];

    if (capturedImage && processedImage) {
      finalPages.push({
        id: `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        capturedImage,
        processedImage,
        timestamp: Date.now(),
      });
    }

    if (finalPages.length === 0) {
      setError('No pages to finalize');
      return;
    }

    try {
      setState('processing');
      setError(null);

      // Create multi-page PDF using shared utilities
      let pdf = null;

      for (let i = 0; i < finalPages.length; i++) {
        const page = finalPages[i];

        // Load image
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error(`Failed to load page ${i + 1}`));
          img.src = page.processedImage;
        });

        // Create canvas from image
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        ctx.drawImage(img, 0, 0);

        // Convert canvas to image data
        const imgData = canvas.toDataURL('image/jpeg', PDF_QUALITY);

        // Create PDF on first page, add new page for subsequent pages
        if (i === 0) {
          const orientation = getOptimalOrientation(
            canvas.width,
            canvas.height
          );
          pdf = createPDFInstance(orientation);
        } else if (pdf) {
          pdf.addPage();
        }

        // Add image to current page
        if (pdf) {
          addImageToPDF(pdf, imgData, 'JPEG', canvas.width, canvas.height);
        }
      }

      // Convert PDF to Blob
      if (!pdf) {
        throw new Error('Failed to create PDF');
      }

      const pdfBlob = pdf.output('blob');

      // Create File object from Blob
      const filename = generateScanFilename();
      const file = new File([pdfBlob], filename, {
        type: 'application/pdf',
        lastModified: Date.now(),
      });

      // Call completion callback
      onScanComplete?.(file);

      // Reset state
      setState('idle');
      setScannedPages([]);
      setCapturedImage(null);
      setProcessedImage(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create multi-page PDF';
      setError(errorMessage);
      setState('error');
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    }
  }, [scannedPages, capturedImage, processedImage, onScanComplete, onError]);

  const retake = useCallback(() => {
    setCapturedImage(null);
    setProcessedImage(null);
    setState('camera');
    setError(null);
  }, []);

  const reset = useCallback(() => {
    stopCameraStream(stream);
    setStream(null);
    setCapturedImage(null);
    setProcessedImage(null);
    setScannedPages([]);
    setState('idle');
    setError(null);
  }, [stream]);

  useEffect(() => {
    return () => {
      if (stream) {
        stopCameraStream(stream);
      }
    };
  }, [stream]);

  return {
    // State
    state,
    error,
    stream,
    capturedImage,
    processedImage,
    scannedPages,
    documentMode,
    // Refs
    videoRef,
    canvasRef,
    // Actions
    initializeScanner,
    captureImage,
    highlightDocument,
    setDocumentMode,
    // Preview actions
    acceptScan,
    retake,
    // Summary/batch actions
    goToSummary,
    addPageToBatch,
    addMorePages,
    removePageFromBatch,
    finalizeBatchScan,
    // Reset
    reset,
  };
};
